import { useState } from 'react';
import { Book, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Scope, type ApiScope } from '@prolarus/shared';

interface CodeBlockProps {
  code: string;
  language?: string;
}

function CodeBlock({ code, language = 'bash' }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
        <code className={`language-${language}`}>{code}</code>
      </pre>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-6 w-6"
        onClick={copyToClipboard}
      >
        {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
      </Button>
    </div>
  );
}

interface EndpointProps {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  description: string;
  scope: ApiScope;
  requestBody?: string;
  responseBody: string;
  queryParams?: { name: string; description: string; required?: boolean }[];
}

function Endpoint({
  method,
  path,
  description,
  scope,
  requestBody,
  responseBody,
  queryParams,
}: EndpointProps) {
  const [isOpen, setIsOpen] = useState(false);

  const methodColors: Record<string, string> = {
    GET: 'bg-blue-500',
    POST: 'bg-green-500',
    PUT: 'bg-amber-500',
    DELETE: 'bg-red-500',
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center gap-3 p-3 hover:bg-muted rounded-lg transition-colors">
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Badge className={methodColors[method]}>{method}</Badge>
          <code className="text-sm font-medium">{path}</code>
          <span className="text-muted-foreground text-sm flex-1 text-left">{description}</span>
          <Badge variant="outline" className="text-xs">
            {scope}
          </Badge>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-8 pr-4 pb-4 space-y-4">
          {queryParams && queryParams.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2">Query Parameters</h4>
              <div className="space-y-1">
                {queryParams.map((param) => (
                  <div key={param.name} className="flex items-center gap-2 text-sm">
                    <code className="bg-muted px-2 py-0.5 rounded">{param.name}</code>
                    {param.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                    <span className="text-muted-foreground">{param.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {requestBody && (
            <div>
              <h4 className="text-sm font-medium mb-2">Request Body</h4>
              <CodeBlock code={requestBody} language="json" />
            </div>
          )}
          <div>
            <h4 className="text-sm font-medium mb-2">Response</h4>
            <CodeBlock code={responseBody} language="json" />
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function ApiDocumentation() {
  const baseUrl = `${import.meta.env.VITE_API_URL}/api/v1/external`;

  return (
    <div className="space-y-6">
      {/* Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Book className="h-5 w-5" />
            API Documentation
          </CardTitle>
          <CardDescription>
            Complete reference for the REST API. Use API keys to authenticate requests.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Base URL */}
          <div>
            <h3 className="text-lg font-semibold mb-2">Base URL</h3>
            <CodeBlock code={baseUrl} />
          </div>

          {/* Authentication */}
          <div>
            <h3 className="text-lg font-semibold mb-2">Authentication</h3>
            <p className="text-muted-foreground mb-3">
              All API requests require an API key passed in the Authorization header as a Bearer
              token.
            </p>
            <CodeBlock
              code={`curl -X GET "${baseUrl}/vendors" \\
  -H "Authorization: Bearer pk_live_your_api_key_here" \\
  -H "Content-Type: application/json"`}
            />
          </div>

          {/* Rate Limiting */}
          <div>
            <h3 className="text-lg font-semibold mb-2">Rate Limiting</h3>
            <p className="text-muted-foreground">
              API requests are limited to <strong>1,000 requests per hour</strong> per API key
              (bucketed per key; unauthenticated callers bucket per client IP). Rate-limit
              metadata is included on every response:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
              <li>
                <code className="bg-muted px-1 rounded">x-ratelimit-limit</code>: Total requests
                allowed in the window
              </li>
              <li>
                <code className="bg-muted px-1 rounded">x-ratelimit-remaining</code>: Requests
                remaining in the current window
              </li>
              <li>
                <code className="bg-muted px-1 rounded">x-ratelimit-reset</code>: Seconds until
                the window resets
              </li>
              <li>
                <code className="bg-muted px-1 rounded">retry-after</code>: Seconds to wait before
                retrying (only on a 429 response)
              </li>
            </ul>
          </div>

          {/* Pagination */}
          <div>
            <h3 className="text-lg font-semibold mb-2">Pagination & response shapes</h3>
            <p className="text-muted-foreground mb-3">
              List endpoints support pagination via query parameters:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>
                <code className="bg-muted px-1 rounded">page</code>: Page number (default: 1)
              </li>
              <li>
                <code className="bg-muted px-1 rounded">limit</code>: Records per page (default:
                50, max: 100)
              </li>
              <li>
                <code className="bg-muted px-1 rounded">sort</code>: Field to sort by — supported
                on the
                <code className="mx-1 bg-muted px-1 rounded">/vendors</code>,
                <code className="mx-1 bg-muted px-1 rounded">/price-books</code>, and
                <code className="mx-1 bg-muted px-1 rounded">/invoices</code>
                roots only. Picklists, statuses, and item-list endpoints sort by their natural
                order (<code className="bg-muted px-1 rounded">sequence</code> for picklists and
                items; <code className="bg-muted px-1 rounded">seq_num</code> for statuses).
              </li>
              <li>
                <code className="bg-muted px-1 rounded">order</code>: Sort order
                (<code className="bg-muted px-1 rounded">asc</code>/<code className="bg-muted px-1 rounded">desc</code>)
                — paired with <code className="bg-muted px-1 rounded">sort</code> on the same
                three resource roots.
              </li>
            </ul>
            <p className="text-muted-foreground mt-3 text-sm">
              List responses always wrap data in
              <code className="mx-1 bg-muted px-1 rounded">{'{ data, pagination: { page, limit, total, total_pages } }'}</code>.
              Single-resource <code className="bg-muted px-1 rounded">GET /:id</code> endpoints
              for vendors, price-books, and invoices return the row directly with no envelope;
              picklists and statuses return
              <code className="mx-1 bg-muted px-1 rounded">{'{ data: row }'}</code>. POST/PUT/DELETE
              shapes vary per endpoint — see each one for the exact response.
            </p>
          </div>

          {/* Error Handling */}
          <div>
            <h3 className="text-lg font-semibold mb-2">Error Handling</h3>
            <p className="text-muted-foreground mb-3">
              Errors are returned with an appropriate HTTP status code and a JSON body containing
              an <code className="bg-muted px-1 rounded">error</code> string. Additional context
              fields may be included depending on the error class:
            </p>
            <CodeBlock
              code={`// Generic error
{ "error": "Error message describing what went wrong" }

// Validation failure on a single object (POST single, PUT /:id)
{ "error": "Validation failed",
  "details": ["field: message", "..."] }

// Validation failure on a bulk array (POST array, PUT bulk)
{ "error": "Validation failed",
  "details": [{ "index": 0, "errors": ["field: message"] }] }

// Bulk reference 404 (PUT bulk with unknown id)
{ "error": "Some vendors not found or do not belong to your company",
  "missing_ids": ["uuid-x"] }

// Bulk DELETE with malformed UUID(s) — nothing is deleted
{ "error": "Some item IDs are not valid UUIDs; no items were deleted",
  "invalid_ids": ["not-a-uuid"] }

// Invoices write with internal-only fields (FORBIDDEN_FIELDS guard)
{ "error": "These fields cannot be set via the external API",
  "forbidden_fields": ["extraction_method", "ai_provider"] }

// Unknown status alias on GET filter / POST / PUT
{ "error": "Invalid status_unique_name: not_a_real_alias" }`}
              language="json"
            />
            <div className="mt-3 space-y-1 text-sm text-muted-foreground">
              <div>
                <Badge variant="secondary">400</Badge> Bad Request - Invalid input
              </div>
              <div>
                <Badge variant="secondary">401</Badge> Unauthorized - Invalid or missing API key
              </div>
              <div>
                <Badge variant="secondary">403</Badge> Forbidden - Missing required scope
              </div>
              <div>
                <Badge variant="secondary">404</Badge> Not Found - Resource doesn't exist
              </div>
              <div>
                <Badge variant="secondary">405</Badge> Method Not Allowed - e.g., POST/PUT/DELETE on a read-only resource
              </div>
              <div>
                <Badge variant="secondary">429</Badge> Too Many Requests - Rate limit exceeded
              </div>
              <div>
                <Badge variant="secondary">500</Badge> Internal Server Error
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vendors Endpoints */}
      <Card>
        <CardHeader>
          <CardTitle>Vendors</CardTitle>
          <CardDescription>Manage vendor records</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Endpoint
            method="GET"
            path="/vendors"
            description="List all vendors (paginated)"
            scope={Scope.VENDORS_READ}
            queryParams={[
              { name: 'page', description: 'Page number (default: 1)' },
              { name: 'limit', description: 'Records per page (default: 50, max: 100)' },
              { name: 'status_id', description: 'Filter by status UUID' },
              { name: 'status_unique_name', description: 'Filter by status unique name (e.g., "active"). Unknown alias → 400.' },
              { name: 'sort', description: 'Sort field (default: created)' },
              { name: 'order', description: 'Sort order: asc | desc (default: desc)' },
            ]}
            responseBody={`{
  "data": [
    {
      "id": "uuid",
      "name": "Acme Corp",
      "address": "123 Main St",
      "city": "Houston",
      "state": "TX",
      "country": "United States",
      "phone": "555-1234",
      "email": null,
      "status_id": "status-uuid",
      "current_status": { "id": "status-uuid", "name": "Active" },
      "external_system": null,
      "external_system_record_id": null,
      "created": "2026-01-07T21:00:00Z",
      "modified": "2026-01-07T21:00:00Z",
      "created_by": "user-uuid",
      "updated_by": "user-uuid"
    }
  ],
  "pagination": { "page": 1, "limit": 50, "total": 127, "total_pages": 3 }
}`}
          />
          <Endpoint
            method="GET"
            path="/vendors/:id"
            description="Get a single vendor (returns the row directly, no envelope)"
            scope={Scope.VENDORS_READ}
            responseBody={`{
  "id": "uuid",
  "name": "Acme Corp",
  "address": "123 Main St",
  "city": "Houston",
  "state": "TX",
  "country": "United States",
  "phone": "555-1234",
  "email": null,
  "status_id": "status-uuid",
  "current_status": { "id": "status-uuid", "name": "Active" },
  "external_system": null,
  "external_system_record_id": null,
  "created": "2026-01-07T21:00:00Z",
  "modified": "2026-01-07T21:00:00Z",
  "created_by": "user-uuid",
  "updated_by": "user-uuid"
}

// 404
{ "error": "Vendor not found" }`}
          />
          <Endpoint
            method="POST"
            path="/vendors"
            description="Create vendor(s) — single object OR array (max 100)"
            scope={Scope.VENDORS_WRITE}
            requestBody={`// Single — returns the unwrapped row at status 201
{
  "name": "Acme Corp",
  "address": "123 Main St",
  "city": "Houston",
  "state": "TX",
  "country": "United States",
  "phone": "555-1234",
  "email": "ops@acme.com",
  "status_id": "status-uuid"
  // OR use status_unique_name instead of status_id:
  // "status_unique_name": "active"
}

// Bulk — array of up to 100 vendors
[
  { "name": "Acme Corp", "city": "Houston", "state": "TX", "status_unique_name": "active" },
  { "name": "Beta Inc", "city": "Dallas", "state": "TX", "status_unique_name": "pending" }
]

// If status_id and status_unique_name are both omitted, the default vendor status is auto-assigned.`}
            responseBody={`// Single → 201 (unwrapped row)
{
  "id": "new-uuid",
  "name": "Acme Corp",
  "address": "123 Main St",
  "city": "Houston",
  "state": "TX",
  "country": "United States",
  "phone": "555-1234",
  "email": "ops@acme.com",
  "status_id": "status-uuid",
  "external_system": null,
  "external_system_record_id": null,
  "created": "2026-01-07T21:00:00Z",
  "modified": "2026-01-07T21:00:00Z",
  "created_by": "user-uuid",
  "updated_by": null
}

// Bulk → 201
{ "created": 2, "vendors": [ /* full rows */ ] }

// 400 — single object validation failure
{ "error": "Validation failed", "details": ["name: Vendor name is required"] }

// 400 — bulk array validation failure
{ "error": "Validation failed",
  "details": [{ "index": 1, "errors": ["name: Vendor name is required"] }] }`}
          />
          <Endpoint
            method="PUT"
            path="/vendors"
            description="Bulk update — array of { id, ...partial } (max 100). No single PUT on this resource."
            scope={Scope.VENDORS_WRITE}
            requestBody={`[
  {
    "id": "vendor-uuid-1",
    "name": "Updated Acme Corp",
    "city": "Austin",
    "phone": "555-5678"
  },
  {
    "id": "vendor-uuid-2",
    "status_unique_name": "inactive"
  }
]`}
            responseBody={`{ "updated": 2, "vendors": [ /* full updated rows */ ] }

// 404 — any id missing or not belonging to your company
{ "error": "Some vendors not found or do not belong to your company",
  "missing_ids": ["uuid-x"] }

// 400 — per-row validation failure
{ "error": "Validation failed",
  "details": [{ "index": 0, "errors": ["id is required and must be a valid UUID"] }] }`}
          />
        </CardContent>
      </Card>

      {/* Price Books Endpoints */}
      <Card>
        <CardHeader>
          <CardTitle>Price Books</CardTitle>
          <CardDescription>Manage price books and their items</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Endpoint
            method="GET"
            path="/price-books"
            description="List all price books (paginated)"
            scope={Scope.PRICE_BOOKS_READ}
            queryParams={[
              { name: 'page', description: 'Page number (default: 1)' },
              { name: 'limit', description: 'Records per page (default: 50, max: 100)' },
              { name: 'status_id', description: 'Filter by status UUID' },
              { name: 'status_unique_name', description: 'Filter by status unique name. Unknown alias → 400.' },
              { name: 'sort', description: 'Sort field (default: created)' },
              { name: 'order', description: 'Sort order: asc | desc (default: desc)' },
            ]}
            responseBody={`{
  "data": [
    {
      "id": "uuid",
      "name": "Q1 2026 Pricing",
      "vendor_id": "vendor-uuid",
      "region_id": "region-uuid",
      "segment_id": "segment-uuid",
      "effective_date": "2026-01-01",
      "expiry_date": "2026-12-31",
      "status_id": "status-uuid",
      "current_status": { "id": "status-uuid", "name": "Active" },
      "external_system": null,
      "external_system_record_id": null,
      "created": "2026-01-07T21:00:00Z",
      "modified": "2026-01-07T21:00:00Z",
      "created_by": "user-uuid",
      "updated_by": "user-uuid"
    }
  ],
  "pagination": { "page": 1, "limit": 50, "total": 10, "total_pages": 1 }
}`}
          />
          <Endpoint
            method="GET"
            path="/price-books/:id"
            description="Get a single price book (returns the row directly, no envelope)"
            scope={Scope.PRICE_BOOKS_READ}
            responseBody={`{
  "id": "uuid",
  "name": "Q1 2026 Pricing",
  "vendor_id": "vendor-uuid",
  "region_id": "region-uuid",
  "segment_id": "segment-uuid",
  "effective_date": "2026-01-01",
  "expiry_date": "2026-12-31",
  "status_id": "status-uuid",
  "current_status": { "id": "status-uuid", "name": "Active" },
  "external_system": null,
  "external_system_record_id": null,
  "created": "2026-01-07T21:00:00Z",
  "modified": "2026-01-07T21:00:00Z",
  "created_by": "user-uuid",
  "updated_by": "user-uuid"
}

// 404
{ "error": "Price book not found" }`}
          />
          <Endpoint
            method="POST"
            path="/price-books"
            description="Create a price book (single only — no bulk on this resource root)"
            scope={Scope.PRICE_BOOKS_WRITE}
            requestBody={`{
  "name": "Q1 2026 Pricing",
  "vendor_id": "vendor-uuid",
  "effective_date": "2026-01-01",
  "expiry_date": "2026-12-31",
  "region_id": "region-uuid",
  "segment_id": "segment-uuid",
  "status_id": "status-uuid"
  // OR use status_unique_name instead of status_id:
  // "status_unique_name": "active"
}

// Required: name, vendor_id (UUID), effective_date (YYYY-MM-DD).
// expiry_date must be >= effective_date when both are sent.
// If status_id and status_unique_name are both omitted, the default price-book status is auto-assigned.`}
            responseBody={`// 201 — unwrapped row
{
  "id": "uuid",
  "name": "Q1 2026 Pricing",
  "vendor_id": "vendor-uuid",
  "region_id": "region-uuid",
  "segment_id": "segment-uuid",
  "effective_date": "2026-01-01",
  "expiry_date": "2026-12-31",
  "status_id": "status-uuid",
  "external_system": null,
  "external_system_record_id": null,
  "created": "2026-01-07T21:00:00Z",
  "modified": "2026-01-07T21:00:00Z",
  "created_by": "user-uuid",
  "updated_by": null
}

// 400 — validation failure
{ "error": "Validation failed",
  "details": ["vendor_id: Invalid UUID format"] }`}
          />
          <Endpoint
            method="PUT"
            path="/price-books/:id"
            description="Update a single price book (partial body)"
            scope={Scope.PRICE_BOOKS_WRITE}
            requestBody={`{
  "name": "Q1 2026 Pricing — Revised",
  "expiry_date": "2027-03-31"
  // Any subset of: name, vendor_id, effective_date, expiry_date,
  // region_id, segment_id, status_id, status_unique_name,
  // external_system, external_system_record_id.
}`}
            responseBody={`{
  "data": { /* full updated row */ },
  "changesLogged": true
}

// 404
{ "error": "Price book not found" }

// 400 — validation failure
{ "error": "Validation failed", "details": ["..."] }`}
          />
          <Endpoint
            method="DELETE"
            path="/price-books/:id"
            description="Delete a single price book"
            scope={Scope.PRICE_BOOKS_WRITE}
            responseBody={`{ "success": true, "changesLogged": true }

// 404
{ "error": "Price book not found" }`}
          />
          <Endpoint
            method="GET"
            path="/price-books/:id/items"
            description="List items for a price book (paginated, sorted by sequence asc)"
            scope={Scope.PRICE_BOOKS_READ}
            queryParams={[
              { name: 'page', description: 'Page number (default: 1)' },
              { name: 'limit', description: 'Records per page (default: 50, max: 100)' },
            ]}
            responseBody={`{
  "data": [
    {
      "id": "item-uuid",
      "price_book_id": "price-book-uuid",
      "itemcode": "PUMP-001",
      "description": "Centrifugal Pump",
      "supplier_itemcode": "SUP-001",
      "supplier_description": "Supplier Pump Description",
      "price": 1500.00,
      "discount": 0,
      "unit": "EA",
      "uom_id": "uom-uuid",
      "is_active": true,
      "locked_when_used": true,
      "sequence": 1,
      "external_system": null,
      "external_system_record_id": null,
      "created": "2026-01-07T21:00:00Z",
      "modified": "2026-01-07T21:00:00Z",
      "created_by": "user-uuid",
      "updated_by": "user-uuid"
    }
  ],
  "pagination": { "page": 1, "limit": 50, "total": 25, "total_pages": 1 }
}`}
          />
          <Endpoint
            method="POST"
            path="/price-books/:id/items"
            description="Create items for a price book (bulk array, max 500)"
            scope={Scope.PRICE_BOOKS_WRITE}
            requestBody={`[
  {
    "itemcode": "PUMP-001",
    "description": "Centrifugal Pump",
    "supplier_itemcode": "SUP-001",
    "supplier_description": "Supplier Pump Description",
    "price": 1500.00,
    "discount": 0,
    "unit": "EA",
    "uom_id": "uom-uuid",
    "is_active": true,
    "locked_when_used": false
  },
  {
    "itemcode": "VALVE-001",
    "description": "Gate Valve",
    "price": 250.00,
    "unit": "EA"
  }
]

// price_book_id is taken from the URL — do not send it in the body.
// sequence is auto-assigned per row, starting from current max + 1.`}
            responseBody={`// 201
{
  "created": 2,
  "items": [
    {
      "id": "item-uuid-1",
      "price_book_id": "price-book-uuid",
      "itemcode": "PUMP-001",
      "description": "Centrifugal Pump",
      "supplier_itemcode": "SUP-001",
      "supplier_description": "Supplier Pump Description",
      "price": 1500.00,
      "discount": 0,
      "unit": "EA",
      "uom_id": "uom-uuid",
      "is_active": true,
      "locked_when_used": false,
      "sequence": 1,
      "external_system": null,
      "external_system_record_id": null,
      "created": "2026-01-07T21:00:00Z",
      "modified": "2026-01-07T21:00:00Z",
      "created_by": "user-uuid",
      "updated_by": null
    }
  ]
}

// 400 — per-row validation failure
{ "error": "Validation failed",
  "details": [{ "index": 0, "errors": ["price: Expected number, received string"] }] }`}
          />
          <Endpoint
            method="PUT"
            path="/price-books/:id/items"
            description="Bulk update price book items (max 500)"
            scope={Scope.PRICE_BOOKS_WRITE}
            requestBody={`[
  {
    "id": "item-uuid-1",
    "itemcode": "PUMP-001-REV",
    "price": 1600.00,
    "discount": 5
  },
  {
    "id": "item-uuid-2",
    "price": 275.00,
    "discount": 10
  }
]`}
            responseBody={`{ "updated": 2, "items": [ /* full updated rows */ ] }

// 404 — any id missing or not in this price book
{ "error": "Some items not found or do not belong to this price book",
  "missing_ids": ["uuid-x"] }

// 400 — per-row validation failure
{ "error": "Validation failed",
  "details": [{ "index": 0, "errors": ["id is required and must be a valid UUID"] }] }`}
          />
          <Endpoint
            method="DELETE"
            path="/price-books/:id/items"
            description="Bulk delete price book items (body is an array of UUIDs, max 500)"
            scope={Scope.PRICE_BOOKS_WRITE}
            requestBody={`["item-uuid-1", "item-uuid-2", "item-uuid-3"]`}
            responseBody={`// 200 — actual delete count + any well-formed UUIDs that didn't match
{ "deleted": 2, "missing_ids": ["item-uuid-3"] }

// 400 — any malformed UUID rejects the whole request, nothing is deleted
{ "error": "Some item IDs are not valid UUIDs; no items were deleted",
  "invalid_ids": ["not-a-uuid"] }`}
          />
        </CardContent>
      </Card>

      {/* Invoices Endpoints */}
      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
          <CardDescription>
            Manage invoices and their line items. Five fields are blocked from external writes —
            <code className="mx-1 bg-muted px-1 rounded">extraction_method</code>,
            <code className="mx-1 bg-muted px-1 rounded">extraction_confidence</code>,
            <code className="mx-1 bg-muted px-1 rounded">ai_provider</code>,
            <code className="mx-1 bg-muted px-1 rounded">source_filename</code>,
            <code className="bg-muted px-1 rounded">source_file_path</code> — they attribute
            rows to AI extraction runs and would falsely claim AI provenance if set externally.
            POST or PUT containing any of them returns 400 with
            <code className="mx-1 bg-muted px-1 rounded">{'{ error, forbidden_fields: [...] }'}</code>.
            They appear in read responses — the row may have been populated by an internal AI
            extraction run.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Endpoint
            method="GET"
            path="/invoices"
            description="List all invoices (paginated)"
            scope={Scope.INVOICES_READ}
            queryParams={[
              { name: 'page', description: 'Page number (default: 1)' },
              { name: 'limit', description: 'Records per page (default: 50, max: 100)' },
              { name: 'status_id', description: 'Filter by status UUID' },
              { name: 'status_unique_name', description: 'Filter by status unique name (e.g., "pending"). Unknown alias → 400.' },
              { name: 'sort', description: 'Sort field (default: created)' },
              { name: 'order', description: 'Sort order: asc | desc (default: desc)' },
            ]}
            responseBody={`{
  "data": [
    {
      "id": "uuid",
      "invoice_number": "INV-2026-001",
      "vendor_id": "vendor-uuid",
      "vendor": { "name": "Acme Corp" },
      "price_book_id": "price-book-uuid",
      "invoice_date": "2026-01-07",
      "service_date": "2026-01-05",
      "due_date": "2026-02-07",
      "subtotal": 4500.00,
      "tax_amount": 500.00,
      "discount": 5,
      "discount_amt": 250.00,
      "discount_expiration": "2026-01-17",
      "total_amount": 5000.00,
      "currency": "USD",
      "payment_terms": "Net 30",
      "po_number": "PO-12345",
      "notes": "Payment due on receipt",
      "status_id": "status-uuid",
      "current_status": { "id": "status-uuid", "name": "Pending" },
      "quote_id": null,
      "quote_version_id": null,
      "external_system": null,
      "external_system_record_id": null,
      "source_filename": "invoice.pdf",
      "source_file_path": "invoices/invoice.pdf",
      "extraction_method": "ai",
      "extraction_confidence": 95,
      "ai_provider": "openai",
      "created": "2026-01-07T21:00:00Z",
      "modified": "2026-01-07T21:00:00Z",
      "created_by": "user-uuid",
      "updated_by": "user-uuid"
    }
  ],
  "pagination": { "page": 1, "limit": 50, "total": 100, "total_pages": 2 }
}`}
          />
          <Endpoint
            method="GET"
            path="/invoices/:id"
            description="Get a single invoice (returns the row directly, no envelope)"
            scope={Scope.INVOICES_READ}
            responseBody={`{
  "id": "uuid",
  "invoice_number": "INV-2026-001",
  "vendor_id": "vendor-uuid",
  "vendor": { "name": "Acme Corp" },
  "current_status": { "id": "status-uuid", "name": "Pending" }
  /* ...all other invoice fields, see GET /invoices for the full shape... */
}

// 404
{ "error": "Invoice not found" }`}
          />
          <Endpoint
            method="POST"
            path="/invoices"
            description="Create an invoice (single only — no bulk on this resource root)"
            scope={Scope.INVOICES_WRITE}
            requestBody={`{
  "vendor_id": "vendor-uuid",
  "price_book_id": "price-book-uuid",
  "invoice_number": "INV-2026-001",
  "invoice_date": "2026-01-07",
  "service_date": "2026-01-05",
  "due_date": "2026-02-07",
  "subtotal": 4500.00,
  "tax_amount": 500.00,
  "discount": 5,
  "discount_amt": 250.00,
  "discount_expiration": "2026-01-17",
  "total_amount": 5000.00,
  "currency": "USD",
  "payment_terms": "Net 30",
  "po_number": "PO-12345",
  "notes": "Payment due on receipt",
  "status_id": "status-uuid"
  // OR use status_unique_name instead of status_id:
  // "status_unique_name": "pending"
}

// All fields are optional. If status_id and status_unique_name are both
// omitted, the default invoice status is auto-assigned.
// Sending any of extraction_method, extraction_confidence, ai_provider,
// source_filename, source_file_path returns 400 with forbidden_fields.`}
            responseBody={`// 201 — unwrapped row
{
  "id": "new-uuid",
  "invoice_number": "INV-2026-001",
  /* ...rest of the invoice row... */
  "extraction_method": null,
  "extraction_confidence": null,
  "ai_provider": null,
  "source_filename": null,
  "source_file_path": null,
  "created_by": "user-uuid",
  "updated_by": null
}

// 400 — internal-only field present
{ "error": "These fields cannot be set via the external API",
  "forbidden_fields": ["extraction_method"] }

// 400 — validation failure
{ "error": "Validation failed",
  "details": ["invoice_date: Date must be in YYYY-MM-DD format"] }`}
          />
          <Endpoint
            method="PUT"
            path="/invoices/:id"
            description="Update a single invoice (partial body, FORBIDDEN_FIELDS guarded)"
            scope={Scope.INVOICES_WRITE}
            requestBody={`{
  "po_number": "PO-12346",
  "discount": 7.5,
  "notes": "Net 30 confirmed"
  // Any subset of writeable fields. extraction_method, extraction_confidence,
  // ai_provider, source_filename, source_file_path remain forbidden on update.
}`}
            responseBody={`{
  "data": { /* full updated row */ },
  "changesLogged": true
}

// 404
{ "error": "Invoice not found" }

// 400 — internal-only field present
{ "error": "These fields cannot be set via the external API",
  "forbidden_fields": ["ai_provider"] }`}
          />
          <Endpoint
            method="DELETE"
            path="/invoices/:id"
            description="Delete a single invoice"
            scope={Scope.INVOICES_WRITE}
            responseBody={`{ "success": true, "changesLogged": true }

// 404
{ "error": "Invoice not found" }`}
          />
          <Endpoint
            method="GET"
            path="/invoices/:id/items"
            description="List items for an invoice (paginated, sorted by sequence asc)"
            scope={Scope.INVOICES_READ}
            queryParams={[
              { name: 'page', description: 'Page number (default: 1)' },
              { name: 'limit', description: 'Records per page (default: 50, max: 100)' },
            ]}
            responseBody={`{
  "data": [
    {
      "id": "item-uuid",
      "invoice_id": "invoice-uuid",
      "item_code": "SVC-001",
      "description": "Consulting Services",
      "quantity": 10,
      "unit_price": 150.00,
      "discount": 0,
      "line_total": 1500.00,
      "uom": "HR",
      "price_book_item_id": "pb-item-uuid",
      "quote_item_id": null,
      "sequence": 1,
      "external_system": null,
      "external_system_record_id": null,
      "created": "2026-01-07T21:00:00Z",
      "modified": "2026-01-07T21:00:00Z",
      "created_by": "user-uuid",
      "updated_by": "user-uuid"
    }
  ],
  "pagination": { "page": 1, "limit": 50, "total": 5, "total_pages": 1 }
}`}
          />
          <Endpoint
            method="POST"
            path="/invoices/:id/items"
            description="Create items for an invoice (bulk array, max 500). Auto-recomputes invoice totals."
            scope={Scope.INVOICES_WRITE}
            requestBody={`[
  {
    "item_code": "SVC-001",
    "description": "Consulting Services",
    "quantity": 10,
    "unit_price": 150.00,
    "discount": 0,
    "uom": "HR",
    "price_book_item_id": "pb-item-uuid"
  },
  {
    "description": "Equipment Rental",
    "quantity": 5,
    "unit_price": 200.00
  }
]

// description is required. invoice_id is taken from the URL.
// sequence is auto-assigned per row from current max + 1.
// line_total is server-computed via line_total = quantity * unit_price * (1 - discount/100)
// when not sent. Caller can override by including line_total explicitly.
// On insert, the parent invoice's subtotal/total/discount_amt are recalculated automatically
// (skipped for PDF-extracted invoices).`}
            responseBody={`// 201
{
  "created": 2,
  "items": [
    {
      "id": "new-item-uuid",
      "invoice_id": "invoice-uuid",
      "item_code": "SVC-001",
      "description": "Consulting Services",
      "quantity": 10,
      "unit_price": 150.00,
      "discount": 0,
      "line_total": 1500.00,
      "uom": "HR",
      "price_book_item_id": "pb-item-uuid",
      "sequence": 1,
      "created": "2026-01-07T21:00:00Z",
      "modified": "2026-01-07T21:00:00Z",
      "created_by": "user-uuid",
      "updated_by": null
    }
  ]
}

// 400 — per-row validation failure
{ "error": "Validation failed",
  "details": [{ "index": 0, "errors": ["description: String must contain at least 1 character(s)"] }] }`}
          />
          <Endpoint
            method="PUT"
            path="/invoices/:id/items"
            description="Bulk update invoice items (max 500). Auto-recomputes invoice totals."
            scope={Scope.INVOICES_WRITE}
            requestBody={`[
  {
    "id": "item-uuid-1",
    "quantity": 12
    // line_total is server-recomputed because quantity changed and line_total wasn't sent.
  },
  {
    "id": "item-uuid-2",
    "unit_price": 175.00,
    "discount": 5
  }
]

// When any of quantity / unit_price / discount is in the patch and
// line_total isn't sent, the server recomputes line_total against
// the row's current values + the patch.`}
            responseBody={`{ "updated": 2, "items": [ /* full updated rows */ ] }

// 404 — any id missing or not in this invoice
{ "error": "Some items not found or do not belong to this invoice",
  "missing_ids": ["uuid-x"] }

// 400 — per-row validation failure
{ "error": "Validation failed",
  "details": [{ "index": 0, "errors": ["id is required and must be a valid UUID"] }] }`}
          />
          <Endpoint
            method="DELETE"
            path="/invoices/:id/items"
            description="Bulk delete invoice items (body is an array of UUIDs, max 500). Auto-recomputes invoice totals."
            scope={Scope.INVOICES_WRITE}
            requestBody={`["item-uuid-1", "item-uuid-2", "item-uuid-3"]`}
            responseBody={`// 200 — actual delete count + any well-formed UUIDs that didn't match
{ "deleted": 2, "missing_ids": ["item-uuid-3"] }

// 400 — any malformed UUID rejects the whole request, nothing is deleted
{ "error": "Some item IDs are not valid UUIDs; no items were deleted",
  "invalid_ids": ["not-a-uuid"] }`}
          />
        </CardContent>
      </Card>

      {/* Picklist Endpoints */}
      <Card>
        <CardHeader>
          <CardTitle>Picklists (Basins, Regions, Segments)</CardTitle>
          <CardDescription>
            All three picklist resources share an identical schema and endpoint set. Replace
            <code className="mx-1 bg-muted px-1 rounded">{'{resource}'}</code>with one of
            <code className="mx-1 bg-muted px-1 rounded">basins</code>,
            <code className="mx-1 bg-muted px-1 rounded">regions</code>, or
            <code className="bg-muted px-1 rounded">segments</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Endpoint
            method="GET"
            path="/{resource}"
            description="List items (paginated)"
            scope={Scope.PICKLISTS_READ}
            queryParams={[
              { name: 'page', description: 'Page number (default: 1)' },
              { name: 'limit', description: 'Records per page (default: 50, max: 100)' },
              { name: 'is_active', description: 'Filter by active status (true/false)' },
            ]}
            responseBody={`{
  "data": [
    {
      "id": "uuid",
      "display_value": "Permian Basin",
      "storage_value": "permian_basin",
      "sequence": 1,
      "is_active": true,
      "is_default": false,
      "created": "2026-05-01T21:00:00Z",
      "modified": "2026-05-01T21:00:00Z",
      "created_by": "user-uuid",
      "updated_by": "user-uuid"
    }
  ],
  "pagination": { "page": 1, "limit": 50, "total": 10, "total_pages": 1 }
}`}
          />
          <Endpoint
            method="GET"
            path="/{resource}/:id"
            description="Get a single item by UUID"
            scope={Scope.PICKLISTS_READ}
            responseBody={`{
  "data": {
    "id": "uuid",
    "display_value": "Permian Basin",
    "storage_value": "permian_basin",
    "sequence": 1,
    "is_active": true,
    "is_default": false,
    "created": "2026-05-01T21:00:00Z",
    "modified": "2026-05-01T21:00:00Z",
    "created_by": "user-uuid",
    "updated_by": "user-uuid"
  }
}`}
          />
          <Endpoint
            method="POST"
            path="/{resource}"
            description="Create item(s) — single object OR array (max 100)"
            scope={Scope.PICKLISTS_WRITE}
            requestBody={`// Single
{
  "display_value": "Permian Basin",
  "storage_value": "permian_basin",
  "sequence": 1,
  "is_active": true,
  "is_default": false
}

// OR bulk array (max 100)
[
  { "display_value": "Permian Basin", "storage_value": "permian_basin", "sequence": 1 },
  { "display_value": "Delaware Basin", "storage_value": "delaware_basin", "sequence": 2 }
]`}
            responseBody={`// Single → 201
{
  "data": {
    "id": "new-uuid",
    "display_value": "Permian Basin",
    "storage_value": "permian_basin",
    "sequence": 1,
    "is_active": true,
    "is_default": false,
    "created": "2026-05-01T21:00:00Z",
    "modified": "2026-05-01T21:00:00Z"
  }
}

// Bulk array → 201
{ "created": 2, "items": [ /* full rows */ ] }`}
          />
          <Endpoint
            method="PUT"
            path="/{resource}"
            description="Bulk update — array of { id, ...partial } (max 100)"
            scope={Scope.PICKLISTS_WRITE}
            requestBody={`[
  { "id": "uuid-1", "display_value": "Updated Permian Basin", "sequence": 1 },
  { "id": "uuid-2", "is_active": false }
]`}
            responseBody={`{ "updated": 2, "items": [ /* full rows */ ] }

// 404 if any id is missing or not yours
{ "error": "Some basins not found or do not belong to your company",
  "missing_ids": ["uuid-x"] }`}
          />
          <Endpoint
            method="PUT"
            path="/{resource}/:id"
            description="Update a single item (partial body)"
            scope={Scope.PICKLISTS_WRITE}
            requestBody={`{
  "display_value": "Updated Permian Basin",
  "is_active": true
}`}
            responseBody={`{
  "data": { /* full updated row */ },
  "changesLogged": true
}`}
          />
          <Endpoint
            method="DELETE"
            path="/{resource}"
            description="Bulk delete — body is an array of UUIDs (max 100)"
            scope={Scope.PICKLISTS_WRITE}
            requestBody={`["uuid-1", "uuid-2", "uuid-3"]`}
            responseBody={`// 200 — actual count, plus any well-formed UUIDs that didn't match
{ "deleted": 2, "missing_ids": ["uuid-3"] }

// 400 — any malformed UUID rejects the whole request, nothing is deleted
{ "error": "Some basin IDs are not valid UUIDs; nothing was deleted",
  "invalid_ids": ["not-a-uuid"] }`}
          />
          <Endpoint
            method="DELETE"
            path="/{resource}/:id"
            description="Delete a single item"
            scope={Scope.PICKLISTS_WRITE}
            responseBody={`{ "success": true, "changesLogged": true }`}
          />
        </CardContent>
      </Card>

      {/* Statuses Endpoints */}
      <Card>
        <CardHeader>
          <CardTitle>Statuses (Read-only)</CardTitle>
          <CardDescription>
            Workflow statuses per entity type. Five resources:
            <code className="mx-1 bg-muted px-1 rounded">/statuses-vendor</code>,
            <code className="mx-1 bg-muted px-1 rounded">/statuses-price-book</code>,
            <code className="mx-1 bg-muted px-1 rounded">/statuses-invoice</code>,
            <code className="mx-1 bg-muted px-1 rounded">/statuses-ticket</code>,
            <code className="bg-muted px-1 rounded">/statuses-quote-version</code>.
            All require the <code className="bg-muted px-1 rounded">config:read</code> scope, which
            is NOT in the default API-key scope set — edit your key to enable it.
            Non-GET methods return 405.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Endpoint
            method="GET"
            path="/statuses-{entity}"
            description="Paginated list of statuses for an entity type"
            scope={Scope.CONFIG_READ}
            queryParams={[
              { name: 'page', description: 'Page number (default: 1)' },
              { name: 'limit', description: 'Records per page (default: 50, max: 100)' },
            ]}
            responseBody={`{
  "data": [
    {
      "id": "uuid",
      "name": "Received",
      "unique_name": "received",
      "seq_num": 1,
      "subtext": "Invoice with Buyer.",
      "tooltip": "The invoice has been received and is pending review.",
      "readonly": false,
      "created": "2026-01-28T22:15:21Z",
      "modified": "2026-01-29T18:28:58Z"
    }
  ],
  "pagination": { "page": 1, "limit": 50, "total": 5, "total_pages": 1 }
}`}
          />
          <Endpoint
            method="GET"
            path="/statuses-{entity}/:id"
            description="Get a single status by UUID"
            scope={Scope.CONFIG_READ}
            responseBody={`{
  "data": {
    "id": "uuid",
    "name": "Under Review",
    "unique_name": "under_review",
    "seq_num": 2,
    "subtext": "Invoice is being reviewed.",
    "tooltip": "The invoice is currently under review by the team.",
    "readonly": false,
    "created": "2026-01-28T22:15:21Z",
    "modified": "2026-01-29T18:28:58Z"
  }
}

// 404 when not found
{ "error": "Status not found" }`}
          />
          <Endpoint
            method="GET"
            path="/statuses-{entity}/by-unique-name/:unique_name"
            description="Get a single status by its unique_name alias"
            scope={Scope.CONFIG_READ}
            responseBody={`{
  "data": {
    "id": "uuid",
    "name": "Active",
    "unique_name": "active",
    "seq_num": 1,
    "subtext": "Vendor is active.",
    "tooltip": "This vendor is approved and active for transactions.",
    "readonly": false,
    "created": "2026-01-28T22:15:21Z",
    "modified": "2026-01-29T18:28:58Z"
  }
}`}
          />
        </CardContent>
      </Card>

      {/* Code Examples */}
      <Card>
        <CardHeader>
          <CardTitle>Code Examples</CardTitle>
          <CardDescription>Example code in various languages</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">cURL</h3>
            <CodeBlock
              code={`# List vendors
curl -X GET "${baseUrl}/vendors?page=1&limit=10" \\
  -H "Authorization: Bearer pk_live_your_api_key"

# Create a vendor
curl -X POST "${baseUrl}/vendors" \\
  -H "Authorization: Bearer pk_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "New Vendor", "city": "Houston", "state": "TX"}'

# Bulk update vendors (PUT body is an array)
curl -X PUT "${baseUrl}/vendors" \\
  -H "Authorization: Bearer pk_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '[{"id": "vendor-uuid-1", "city": "Austin"}]'

# Update one invoice (FORBIDDEN_FIELDS-guarded)
curl -X PUT "${baseUrl}/invoices/INVOICE_UUID" \\
  -H "Authorization: Bearer pk_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"po_number": "PO-99999", "discount": 7.5}'

# Bulk delete invoice items (body is a JSON array of UUIDs)
curl -X DELETE "${baseUrl}/invoices/INVOICE_UUID/items" \\
  -H "Authorization: Bearer pk_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '["item-uuid-1", "item-uuid-2"]'

# List all invoice statuses (requires config:read scope)
curl -X GET "${baseUrl}/statuses-invoice" \\
  -H "Authorization: Bearer pk_live_your_api_key"

# Get invoice status by unique_name
curl -X GET "${baseUrl}/statuses-invoice/by-unique-name/under_review" \\
  -H "Authorization: Bearer pk_live_your_api_key"`}
            />
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">JavaScript</h3>
            <CodeBlock
              code={`const API_KEY = 'pk_live_your_api_key';
const BASE_URL = '${baseUrl}';

// List vendors — returns { data, pagination }
const listResponse = await fetch(\`\${BASE_URL}/vendors\`, {
  headers: {
    'Authorization': \`Bearer \${API_KEY}\`,
    'Content-Type': 'application/json'
  }
});
const { data: vendors, pagination } = await listResponse.json();

// Get a single vendor — returns the row directly (no envelope)
const oneResponse = await fetch(\`\${BASE_URL}/vendors/\${vendorId}\`, {
  headers: { 'Authorization': \`Bearer \${API_KEY}\` }
});
const vendor = await oneResponse.json();

// Create a vendor — single object → returns unwrapped row at 201
const newVendor = await fetch(\`\${BASE_URL}/vendors\`, {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${API_KEY}\`,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'New Vendor',
    city: 'Houston',
    state: 'TX'
  })
});

// Bulk update price book items (PUT body is an array)
const updates = await fetch(\`\${BASE_URL}/price-books/\${pbId}/items\`, {
  method: 'PUT',
  headers: {
    'Authorization': \`Bearer \${API_KEY}\`,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify([
    { id: 'item-uuid-1', price: 1600.00 },
    { id: 'item-uuid-2', discount: 10 }
  ])
});
const { updated, items } = await updates.json();

// List all vendor statuses
const statusesResponse = await fetch(\`\${BASE_URL}/statuses-vendor\`, {
  headers: { 'Authorization': \`Bearer \${API_KEY}\` }
});
const { data: statuses } = await statusesResponse.json();

// Get status by unique_name
const activeStatus = await fetch(\`\${BASE_URL}/statuses-vendor/by-unique-name/active\`, {
  headers: { 'Authorization': \`Bearer \${API_KEY}\` }
});
const { data: status } = await activeStatus.json();`}
              language="javascript"
            />
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Python</h3>
            <CodeBlock
              code={`import requests

API_KEY = 'pk_live_your_api_key'
BASE_URL = '${baseUrl}'

headers = {
    'Authorization': f'Bearer {API_KEY}',
    'Content-Type': 'application/json'
}

# List vendors → { data, pagination }
response = requests.get(f'{BASE_URL}/vendors', headers=headers)
payload = response.json()
vendors = payload['data']
pagination = payload['pagination']

# Get a single vendor → the row directly (no envelope)
vendor = requests.get(f'{BASE_URL}/vendors/{vendor_id}', headers=headers).json()

# Create a vendor → unwrapped row at 201
new_vendor = requests.post(
    f'{BASE_URL}/vendors',
    headers=headers,
    json={'name': 'New Vendor', 'city': 'Houston', 'state': 'TX'}
).json()

# Update one invoice (FORBIDDEN_FIELDS-guarded — must avoid extraction_method etc.)
updated = requests.put(
    f'{BASE_URL}/invoices/{invoice_id}',
    headers=headers,
    json={'po_number': 'PO-99999', 'discount': 7.5}
).json()
# updated -> { 'data': {...}, 'changesLogged': True }

# Bulk delete invoice items (body is an array of UUIDs)
result = requests.delete(
    f'{BASE_URL}/invoices/{invoice_id}/items',
    headers=headers,
    json=['item-uuid-1', 'item-uuid-2']
).json()
# result -> { 'deleted': 2, 'missing_ids': [] }

# List all price book statuses
response = requests.get(f'{BASE_URL}/statuses-price-book', headers=headers)
statuses = response.json()['data']

# Get status by unique_name
response = requests.get(
    f'{BASE_URL}/statuses-price-book/by-unique-name/active',
    headers=headers
)
status = response.json()['data']`}
              language="python"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
